import {
  DEFAULT_LANDING_PAGE_CONFIG,
  type LandingPageConfig,
  parseLandingPageConfig,
} from '@provision/landing-page-utils';
import {
  type LandingPageBlockValues,
  type LandingPageSettingsValues,
} from 'src/pages/Settings/memberships/components/landing-page-tab/types';

function trimmed(value: string): string | undefined {
  return value.trim() || undefined;
}

function dropUndefined(record: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(record).filter(([, value]) => value !== undefined));
}

function serializeBlock(values: LandingPageBlockValues): Record<string, unknown> {
  const base = { id: values.id, type: values.type };

  switch (values.type) {
    case 'hero': {
      const video = trimmed(values.heroVideoUrl)
        ? dropUndefined({
            mediaUrl: values.heroVideoUrl.trim(),
            title: trimmed(values.heroVideoTitle),
            caption: trimmed(values.heroVideoCaption),
          })
        : undefined;

      return dropUndefined({
        ...base,
        headline: values.heroHeadline,
        subtext: trimmed(values.heroSubtext),
        mediaKind: values.heroMediaKind,
        video,
        image: trimmed(values.heroImageUrl) ? { url: values.heroImageUrl.trim() } : undefined,
      });
    }
    case 'content':
      return dropUndefined({
        ...base,
        kicker: trimmed(values.contentKicker),
        title: values.contentTitle,
        body: values.contentBody,
        imageUrl: trimmed(values.contentImageUrl),
        mediaPosition: values.contentMediaPosition,
      });
    case 'closingCta':
      return dropUndefined({
        ...base,
        headline: values.ctaHeadline,
        subtext: trimmed(values.ctaSubtext),
        buttonLabel: values.ctaButtonLabel,
      });
    default:
      return base;
  }
}

function serializeSettings(
  values: LandingPageSettingsValues,
  stored: LandingPageConfig['settings'],
): Record<string, unknown> {
  const consultationUrl =
    values.ctaMode === 'consultation' ? trimmed(values.consultationUrl) : undefined;

  return {
    ...stored,
    showPricing: values.showPricing,
    cta: dropUndefined({
      ...stored.cta,
      mode: values.ctaMode,
      consultationUrl,
    }),
    logoSize: values.logoSize,
    colors: dropUndefined({
      ...stored.colors,
      primary: trimmed(values.primaryColor),
      secondary: trimmed(values.secondaryColor),
      accent: trimmed(values.accentColor),
    }),
  };
}

// Merging onto the stored config at every level carries `version` and any unknown keys through.
export function serializeLandingPageConfig({
  storedConfig,
  blocks,
  settings,
}: {
  storedConfig: string | null | undefined;
  blocks: readonly LandingPageBlockValues[];
  settings: LandingPageSettingsValues;
}): string {
  const base = parseLandingPageConfig(storedConfig) ?? DEFAULT_LANDING_PAGE_CONFIG;

  return JSON.stringify({
    ...base,
    blocks: blocks.map(serializeBlock),
    settings: serializeSettings(settings, base.settings),
  });
}
